import Bugsnag, { type Event } from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import type React from 'react'

const isBugsnagMounted = !!window?.bugsnagInfo?.code

let started = false

export const createOnErrorHandler = () => {
	return (event: Event) => {
		const originalError = event.originalError

		// Custom grouping. The typeof guard is load-bearing: bugsnag.log() notifies
		// plain strings, "in" throws on primitives, and Bugsnag sends the event
		// anyway when onError throws, skipping every filter below.
		if (
			originalError &&
			typeof originalError === 'object' &&
			'groupingHash' in originalError &&
			typeof originalError.groupingHash === 'string'
		) {
			event.groupingHash = originalError.groupingHash
		}

		const errorMessage = event?.errors?.[0]?.errorMessage || ''
		const stacktrace = event?.errors?.[0]?.stacktrace || []

		// Filter: ResizeObserver errors
		// Browsers report this as different strings across versions:
		// - "ResizeObserver loop limit exceeded" (older Chrome)
		// - "ResizeObserver loop completed with undelivered notifications" (newer Chrome/Firefox)
		// Both are benign -- the browser delivers the missed entries in the next frame automatically.
		if (errorMessage.includes('ResizeObserver loop')) {
			return false
		}

		// Filter: client-side network failures with no HTTP response.
		// axios reports "AxiosError: Network Error" when a request never completes:
		// connectivity drops, navigating away mid-request, ad-blockers, or Safari
		// aborting in-flight requests on backgrounded tabs. These are not app bugs and
		// get reported in bulk from saga catch blocks via bugsnag.log().
		if (errorMessage.includes('AxiosError: Network Error')) {
			return false
		}

		// Filter: 401 Unauthorized responses. The session/token is no longer valid
		// and the app sends the user back through authentication; nothing actionable
		// is reported. Covers bugsnag.log() message strings and directly notified
		// AxiosError objects. Other statuses stay reported (FRONT-574).
		const originalErrorResponseStatus =
			typeof originalError === 'object' && originalError !== null && 'response' in originalError
				? (originalError as { response?: { status?: unknown } }).response?.status
				: undefined

		if (
			errorMessage.includes('AxiosError: Request failed with status code 401') ||
			originalErrorResponseStatus === 401
		) {
			return false
		}

		// Filter: Browser extension errors
		// Check if error originates from browser extensions
		const hasExtensionCode = stacktrace.some((frame) => {
			const file = frame.file || ''
			return (
				file.includes('webkit-masked-url') ||
				file.includes('chrome-extension://') ||
				file.includes('moz-extension://') ||
				file.includes('safari-extension://') ||
				file.includes('safari-web-extension://')
			)
		})

		if (hasExtensionCode) {
			return false
		}

		// Filter: Obfuscated variable errors (likely from extensions or malware)
		// Examples: "Can't find variable: _0x4761", "Cannot read property '_0x123abc'", "Cannot read properties of undefined (reading '_0x123abc')"
		if (/(?:variable|propert(?:y|ies))[:\s'"(].*?_0x[a-f0-9]+/i.test(errorMessage)) {
			return false
		}

		// Filter: document.body.scrollHeight/scrollWidth errors from inline scripts (likely extensions)
		// Example: "null is not an object (evaluating 'document.body.scrollHeight')" at :1:54
		if (/document\.body\.(scrollHeight|scrollWidth)/i.test(errorMessage)) {
			const hasInlineScriptLocation = stacktrace.some((frame) => {
				const file = frame.file || ''
				// Check if error is from line 1 (inline script injection pattern)
				return file.includes(':1:') || file.endsWith('/:1:')
			})

			if (hasInlineScriptLocation || stacktrace.length === 0) {
				return false
			}
		}

		return true
	}
}

const start = (react: typeof React | undefined) => {
	if (started || !window?.bugsnagInfo?.code) {
		return
	}

	started = true

	Bugsnag.start({
		apiKey: window.bugsnagInfo.code,
		appVersion: window.bugsnagInfo.lokaliseVersion || undefined,
		/*
		 * Make sure that React is passed to "BugsnagPluginReact" here instead of in "createErrorBoundary", because
		 * otherwise React component inside ReactWebComponent is being rerendered every time "attributeChangedCallback"
		 * is called. In other words it is rerendered for every web components attribute.
		 * */
		plugins: react ? [new BugsnagPluginReact(react)] : [],
		enabledErrorTypes: {
			// disabling unhandledRejections as our eslint config doesn't allow this, so any remaining errors originate from browser extensions or 3rd parties
			unhandledRejections: false,
		},
		user: {
			email: window.bugsnagInfo.user || undefined,
		},
		onError: createOnErrorHandler(),
	})

	Bugsnag.addMetadata('Meta', {
		user: {
			team: window.bugsnagInfo?.team,
		},
	})
}

// Make sure that Bugsnag is initialized in legacy views.
start(undefined)

const log = (message: string) => {
	/*
	 * Logging errors in github actions causes the tests to fail. Also when running tests these console logs could get
	 * quite annoying, because they might be triggered by a network error, which might be what is expected and actually
	 * tested during the test. The console output in these cases is quite large, which clutters the test log.
	 * */
	if (process.env.NODE_ENV !== 'test') {
		// eslint-disable-next-line no-console
		// biome-ignore lint/suspicious/noConsole: ignored using `--suppress`
		console.error(message)
	}

	if (isBugsnagMounted) {
		Bugsnag.notify(message)
	}
}

const getReactPlugin = () => {
	if (!isBugsnagMounted) {
		return undefined
	}

	return Bugsnag.getPlugin('react')
}

export default {
	start,
	log,
	getReactPlugin,
}
